import React from 'react';
import { LinkIcon } from '../../../components/icons';
import { CplOrder, ProposalOffer } from '../../../api/client/clientCplService';
import { ProposalData } from './types';
import { getStatusLabels } from './constants';
import { useTranslations } from '../../../contexts/translation.context';
import { getPlatformIcon, transformPlatform, getPlatformIconForRender } from '../../../utils';

export const transformStatus = (
  status: string
): 'active' | 'inactive' | 'pending' | 'suspended' => {
  switch (status) {
    case 'in_progress':
      return 'active';
    case 'waiting':
      return 'pending';
    case 'completed':
      return 'active';
    case 'canceled':
      return 'inactive';
    default:
      return 'pending';
  }
};

export const transformProposalOfferToProposalData = (
  offer: ProposalOffer,
  t: any
): ProposalData => {
  const isSubscription = offer.type === 'subscription';

  const priceTiers = (offer.subs || [])
    .map((tier) => ({
      // У офферов-подписок ступени нет — цена одна, без порога.
      from: tier.ge ?? 0,
      price: parseFloat(tier.price) || 0,
    }))
    .sort((a, b) => a.from - b.from)
    .map((tier, index, sorted) => {
      const nextTier = sorted[index + 1];
      const to = nextTier ? nextTier.from - 1 : offer.subs_max || null;

      return { ...tier, to };
    });

  const firstPricingTier = priceTiers[0] || { from: 0, price: 0, to: null };
  const pricePerSubscriber = firstPricingTier.price;

  // Handle owner data - it can be either a string (ID) or an object
  const ownerId = typeof offer.owner === 'string' ? offer.owner : offer.owner.id;
  const ownerName =
    typeof offer.owner === 'string' ? t('proposals.unknownPartner') : offer.owner.name;
  const ownerAvatar = typeof offer.owner === 'string' ? undefined : offer.owner.avatar;

  // Get real statistics from cpl_statistic (only if owner is an object)
  const cplStats = typeof offer.owner === 'object' ? offer.owner.cpl_statistic : null;
  const totalOrders = cplStats?.total_orders || 0;
  const completedOrders = cplStats?.completed_count || 0;
  const inProgressOrders = cplStats?.in_progress_count || 0;

  // Calculate commission based on price tiers (higher tiers = better commission)
  const calculatedCommission =
    offer.subs.length > 1
      ? Math.floor(10 + offer.subs.length * 2) // Multi-tier = better commission
      : 12; // Single tier = standard commission

  // Calculate success rate based on completed vs total orders
  const successRate = totalOrders > 0 ? Math.round((completedOrders / totalOrders) * 100) : 0;

  // Calculate rating based on success rate and completed orders
  const baseRating = Math.min(50, Math.max(30, successRate * 0.4 + completedOrders * 0.5));
  const averageRating = Math.min(50, baseRating);

  // Calculate conversion rate based on success rate
  const conversionRate = Math.min(25, Math.max(10, successRate * 0.2 + 10));

  return {
    id: offer.id,
    internalId: offer.id.slice(-8), // Use last 8 characters as internal ID
    // Офферы, созданные до появления поля type, считаем CPL.
    offerType: isSubscription ? 'subscription' : 'cpl',
    campaign_types: offer.campaign_types,
    of_models: offer.of_models?.length,
    partner: {
      lvl: offer.owner?.lvl,
      id: ownerId,
      name: ownerName,
      username: ownerName.toLowerCase().replace(/\s+/g, '_'),
      avatar: ownerAvatar || undefined,
      rating: Math.floor((averageRating / 10) * 10) / 10, // Convert to 4.0-5.0 scale
      completedOrders: totalOrders,
      platform: transformPlatform(offer.source),
      platformIcon: getPlatformIcon(offer.source),
    },
    description: offer.description || t('proposals.noDescription'),
    requirements: offer.requirements || t('proposals.noRequirements'),
    price: {
      amount: pricePerSubscriber * firstPricingTier.from, // Calculate total for minimum subscribers
      currency: 'USD',
      pricePerSubscriber: pricePerSubscriber,
      tiers: priceTiers,
    },
    campaign: {
      type: offer.campaign_types.includes('free') ? t('proposals.free') : t('proposals.freeTrial'),
      source: offer.source,
      targetSubscribers: offer?.subs_min ?? 0,
      targetSubscribersMax: offer?.subs_max ?? 0,
      // Для CPL — фанов в день, для подписки — фанов в месяц.
      subsPerDay: offer.subs_pre_day ?? 0,
      estimatedCompletion: Math.ceil(
        (offer.subs_max || firstPricingTier.from) / Math.max(1, offer.subs_pre_day)
      ), // Calculate days based on subs per day
      commission: calculatedCommission,
      conversionRate: conversionRate,
    },
    status: offer.status,
    createdAt: offer.created_at,
    updatedAt: offer.created_at,
    statistics: {
      views: totalOrders * 10 + completedOrders * 5, // Estimate views based on orders
      applications: offer.queue ? offer.queue.length : 0,
      successRate: successRate,
      averageRating: averageRating,
      totalUsers: totalOrders,
      activeOrders: inProgressOrders,
    },
  };
};

export const getStatusLabel = (status: string, t: any): string => {
  const labels = getStatusLabels(t);
  return labels[status as keyof typeof labels] || status;
};

export const getPlatformIconForRenderCustom = (platform: string): React.ReactNode => {
  switch (platform) {
    case 'OnlyFans':
      return React.createElement(LinkIcon, { className: 'w-4 h-4' });
    default:
      return getPlatformIconForRender(platform, 'w-4 h-4');
  }
};

export const formatPrice = (amount: number, currency: string = 'USD'): string => {
  return `$${amount.toFixed(2)}`;
};

export const formatRating = (rating: number): string => {
  return (rating / 10).toFixed(1);
};

export const getTierRangeLabel = (
  tiers: { from: number; to?: number | null }[],
  index: number
): string => {
  const tier = tiers[index];
  const isLastTier = index === tiers.length - 1;
  const isSingleValueRange = tier?.to === tier?.from;

  if (tiers.length === 1 || isLastTier) {
    return `${tier.from}+`;
  }

  if (tier.to && !isSingleValueRange) {
    return `${tier.from} - ${tier.to}`;
  }

  return `${tier.from}+`;
};
